import { Card, CopyableValue, Text } from '@components/ui';
import { formatMoney } from '@features/buyer/hire/formatMoney';
import type { JobDetails } from '@features/buyer/jobs/single-job-detail';
import { isAutomotiveJob } from '@features/jobs';
import { JobLocationMap } from '@features/jobs/job-detail';
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';
import Box from '@mui/material/Box';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface DetailItem {
  label: string;
  value: ReactNode;
}

const linkSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  fontSize: 14,
  fontWeight: 600,
  color: 'primary.main',
  textDecoration: 'none',
  '&:hover': { textDecoration: 'underline' },
};

const listingUrl = (link?: string | null): string | null => {
  const trimmed = link?.trim();
  if (!trimmed) return null;
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const copyable = (value?: string | number | null): ReactNode =>
  value == null || value === '' ? null : <CopyableValue value={value} />;

const DetailList = ({ items }: { items: DetailItem[] }): JSX.Element => (
  <Box component="dl" sx={{ m: 0, display: 'grid', gap: 1.75 }}>
    {items
      .filter((item) => item.value != null && item.value !== '')
      .map((item) => (
        <Box key={item.label} sx={{ minWidth: 0 }}>
          <Text as="dt" variant="overline" tone="muted">
            {item.label}
          </Text>
          <Box
            component="dd"
            sx={{
              m: 0,
              mt: 0.25,
              fontSize: 14,
              fontWeight: 500,
              color: 'text.primary',
              overflowWrap: 'anywhere',
            }}
          >
            {item.value}
          </Box>
        </Box>
      ))}
  </Box>
);

const SectionCard = ({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}): JSX.Element => (
  <Box component="section" aria-labelledby={id}>
    <Card sx={{ p: 2.5 }}>
      <Text as="h2" id={id} variant="subtitle" sx={{ mb: 2, fontWeight: 700 }}>
        {title}
      </Text>
      {children}
    </Card>
  </Box>
);

export const RatingDetailAside = ({
  job,
}: {
  job: JobDetails;
}): JSX.Element => {
  const { t } = useTranslation();
  const listing = listingUrl(job.link);
  const mapQuery =
    job.latitude != null && job.longitude != null
      ? `${job.latitude},${job.longitude}`
      : job.address;
  const mapUrl = mapQuery
    ? `https://maps.google.com?q=${encodeURIComponent(mapQuery)}`
    : null;

  return (
    <Box
      component="aside"
      sx={{ display: 'grid', gap: 2, alignContent: 'start', minWidth: 0 }}
    >
      <SectionCard
        id="rating-detail-vehicle"
        title={t(
          isAutomotiveJob(job) ? 'ratingDetail.vehicle' : 'ratingDetail.item'
        )}
      >
        <DetailList
          items={[
            {
              label: t('ratingDetail.description'),
              value: copyable(job.description),
            },
            { label: t('ratingDetail.year'), value: copyable(job.year) },
            { label: t('ratingDetail.make'), value: copyable(job.make) },
            { label: t('ratingDetail.model'), value: copyable(job.model) },
            { label: t('ratingDetail.vin'), value: copyable(job.vinOrSerial) },
            {
              label: t('ratingDetail.askingPrice'),
              value: copyable(formatMoney(job.askingPrice)),
            },
            {
              label: t('ratingDetail.listing'),
              value: listing ? (
                <Box
                  component="a"
                  href={listing}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    ...linkSx,
                    display: 'inline-block',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                  }}
                >
                  {listing}
                </Box>
              ) : null,
            },
          ]}
        />
      </SectionCard>

      <SectionCard
        id="rating-detail-location"
        title={t('ratingDetail.location')}
      >
        <DetailList
          items={[
            { label: t('ratingDetail.address'), value: copyable(job.address) },
            {
              label: t('ratingDetail.pointOfContact'),
              value: copyable(job.contactName),
            },
            {
              label: t('ratingDetail.phone'),
              value: copyable(job.creatorNumber),
            },
            {
              label: t('ratingDetail.email'),
              value: copyable(job.contactEmail),
            },
          ]}
        />
        {mapUrl ? (
          <Box sx={{ mt: 2.5 }}>
            <Box
              sx={{
                overflow: 'hidden',
                borderRadius: 2,
                border: 1,
                borderColor: 'divider',
                lineHeight: 0,
              }}
            >
              <JobLocationMap
                latitude={job.latitude}
                longitude={job.longitude}
                address={job.address}
                height={200}
              />
            </Box>
            <Box
              component="a"
              href={mapUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={[linkSx, { mt: 1.25 }]}
            >
              {t('ratingDetail.openInMaps')}
              <OpenInNewRounded aria-hidden sx={{ fontSize: 16 }} />
            </Box>
          </Box>
        ) : null}
      </SectionCard>
    </Box>
  );
};
